import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";

// Deliberately not a stored collection — see firestore.rules' Phase 3
// comment. Computed from checklistCompletions/timeCardEntries/
// incidentFlags, which are already correctly scoped: a Facility Admin's
// query (filtered by facilityId) sees every vendor for the cross-vendor
// comparison table; a Vendor Admin's query (filtered by vendorId) only
// ever returns their own data (open decision #4, resolved 2026-09-07).
function scoreFor(vendorKey, completions, timeCards, incidents) {
  const c = completions.filter(x => (x.vendorId || null) === vendorKey);
  const t = timeCards.filter(x => (x.vendorId || null) === vendorKey);
  const i = incidents.filter(x => (x.vendorId || null) === vendorKey);
  const completionPct = c.length
    ? Math.round(c.reduce((sum, x) => {
        const items = x.items || [];
        return sum + (items.length ? items.filter(it => it.done).length / items.length : 0);
      }, 0) / c.length * 100)
    : null;
  return {
    completions: c.length,
    completionPct,
    visits: t.length,
    openVisits: t.filter(x => x.status === "open").length,
    incidents: i.length,
    unresolvedIncidents: i.filter(x => !x.resolved).length,
  };
}

function ScoreRow({ label, score }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div style={{ display: "flex", gap: 20, fontSize: 13, color: C.muted }}>
        <div><b style={{ color: C.navy }}>{score.completionPct ?? "—"}{score.completionPct !== null ? "%" : ""}</b> completion</div>
        <div><b style={{ color: C.navy }}>{score.visits}</b> visits{score.openVisits > 0 ? ` (${score.openVisits} open)` : ""}</div>
        <div><b style={{ color: score.unresolvedIncidents > 0 ? C.red : C.navy }}>{score.incidents}</b> incidents{score.unresolvedIncidents > 0 ? ` (${score.unresolvedIncidents} open)` : ""}</div>
      </div>
    </div>
  );
}

// vendorId: null (Facility Admin) shows every vendor + internal staff
// side by side. A real vendorId (Vendor Admin) shows only their own row.
export default function ScorecardPanel({ facilityId, vendorId, vendors }) {
  const [completions, setCompletions] = useState([]);
  const [timeCards, setTimeCards] = useState([]);
  const [incidents, setIncidents] = useState([]);

  const filterField = vendorId ? "vendorId" : "facilityId";
  const filterValue = vendorId || facilityId;

  useEffect(() => onSnapshot(query(collection(db, "checklistCompletions"), where(filterField, "==", filterValue)), s =>
    setCompletions(s.docs.map(d => d.data()))), [filterField, filterValue]);
  useEffect(() => onSnapshot(query(collection(db, "timeCardEntries"), where(filterField, "==", filterValue)), s =>
    setTimeCards(s.docs.map(d => d.data()))), [filterField, filterValue]);
  useEffect(() => onSnapshot(query(collection(db, "incidentFlags"), where(filterField, "==", filterValue)), s =>
    setIncidents(s.docs.map(d => d.data()))), [filterField, filterValue]);

  if (vendorId) {
    const score = scoreFor(vendorId, completions, timeCards, incidents);
    return (
      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden", maxWidth: 640 }}>
        <ScoreRow label="Your scorecard" score={score} />
        {completions.length === 0 && timeCards.length === 0 && (
          <div style={{ padding: 20, fontSize: 13, color: C.muted }}>No activity logged yet — numbers show up here once your crew starts clocking in and completing checklists.</div>
        )}
      </div>
    );
  }

  const internalScore = scoreFor(null, completions, timeCards, incidents);

  return (
    <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
      {vendors.map(v => (
        <ScoreRow key={v.id} label={`${v.companyName} (${v.trade})`} score={scoreFor(v.id, completions, timeCards, incidents)} />
      ))}
      <ScoreRow label="Internal Staff" score={internalScore} />
      {vendors.length === 0 && (
        <div style={{ padding: 20, fontSize: 13, color: C.muted }}>Add a vendor to start seeing scorecards.</div>
      )}
    </div>
  );
}
